import { EtlState, IEtlMetadata, IExtractEtlMetadata, ILoadMetadata, ITransformEtlMetadata } from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { ETL_STATE_LABELS, EtlStateIcon } from '../EtlStateIcon';
import { styleSheet } from './styles';

// The triangle and octagon glyphs read optically smaller than a circle of the same box, so bump them.
const NODE_SIZE = 16;
const ERROR_NODE_SIZE = 20;

interface IETLStatusProps {
	state: EtlState;
	stageLabel?: string;
}

export const ETLStageStatus: React.FC<IETLStatusProps> = ({ state, stageLabel }) => {
	const isError = state === EtlState.IdleWithError || state === EtlState.SuspendedWithError;
	const stateLabel = ETL_STATE_LABELS[state];

	return (
		<EtlStateIcon
			state={state}
			size={isError ? ERROR_NODE_SIZE : NODE_SIZE}
			label={stageLabel ? `${stageLabel}: ${stateLabel}` : stateLabel}
		/>
	);
};

interface IETLConnectorProps {
	metadata: IExtractEtlMetadata | ITransformEtlMetadata | ILoadMetadata;
}

export const ETLConnector: React.FC<IETLConnectorProps> = ({ metadata }) => {
	const isDashed = metadata.state === EtlState.InProgress;

	return (
		<div className={css(styleSheet.connectorContainer)}>
			<div
				className={css(styleSheet.connector, isDashed ? styleSheet.dashedLine : styleSheet.solidLine)}
				role='connector'
			/>
		</div>
	);
};

interface IETLVisualsProps {
	etl: IEtlMetadata;
}

export const ETLVisuals: React.FC<IETLVisualsProps> = ({ etl }) => {
	const stages = ['extract', 'transform', 'load'] as const;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.stagesContainer)}>
				{stages.map((stage, index) => {
					const metadata = etl[stage];

					return (
						<React.Fragment key={stage}>
							{index !== 0 && <ETLConnector metadata={metadata} />}
							<div className={css(styleSheet.stageContainer)}>
								{stage === 'extract' && etl.preProcess?.state === EtlState.InProgress ? (
									<ETLStageStatus state={etl.preProcess.state} stageLabel='Pre-processing' />
								) : (
									<ETLStageStatus state={metadata.state} stageLabel={stage[0].toUpperCase() + stage.slice(1)} />
								)}
							</div>
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);
};

export const ETLSubConnector = ({ status }: { status: EtlState }) => {
	return (
		<div className={css(styleSheet.substepRow)}>
			<div className={css(styleSheet.substepConnectorContainer)}>
				<div className={css(styleSheet.substepConnector)} />
				<div
					className={css(
						styleSheet.substepStatusIndicator,
						status === EtlState.InProgress && styleSheet.substepStatusActive
					)}
				/>
			</div>
		</div>
	);
};
